/**
 * useNumberConfig.ts — fetch + refetch state for the configured tenant's
 * WhatsApp number wiring (Story N1). Mirrors `useLeadDetail`'s phase model:
 * 401/403 → `unauthorized` (the shared clean screen); any other failure
 * (502 / transport / 5xx) → `error` with retry. There is no 404 case here —
 * the BFF is single-tenant and always addresses the one configured tenant.
 *
 * Exposes `refetch`, called after a successful write (set/change/clear) so
 * the panel reflects the new value without a full reload.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { UnauthorizedError, type ApiClient } from '../../api/client';
import type { TenantNumberConfig } from '../../api/types';
import floating from '../../lib/floating';

export type NumberConfigPhase = 'loading' | 'ready' | 'unauthorized' | 'error';

export interface NumberConfigState {
  config: TenantNumberConfig | null;
  phase: NumberConfigPhase;
  refetch: () => Promise<void>;
}

export const useNumberConfig = (apiClient: ApiClient): NumberConfigState => {
  const [config, setConfig] = useState<TenantNumberConfig | null>(null);
  const [phase, setPhase] = useState<NumberConfigPhase>('loading');

  const mountedRef = useRef(true);
  const requestSeq = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchConfig = useCallback(async () => {
    requestSeq.current += 1;
    const seq = requestSeq.current;
    const isCurrent = () => mountedRef.current && seq === requestSeq.current;
    setPhase('loading');
    try {
      const payload = await apiClient.getNumberConfig();
      if (!isCurrent()) return;
      setConfig(payload);
      setPhase('ready');
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof UnauthorizedError) {
        setPhase('unauthorized');
        return;
      }
      // ApiError (502/404) / transport / 5xx — keep any last-known config on
      // screen but flag the error so the page can offer a retry.
      setPhase('error');
    }
  }, [apiClient]);

  useEffect(() => {
    floating(fetchConfig());
  }, [fetchConfig]);

  return { config, phase, refetch: fetchConfig };
};
